import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Clock, Save, Plus, Trash2, 
  Briefcase, Moon, Dumbbell, Users, BookOpen,
  Palette, UtensilsCrossed, Car, ShoppingCart, Timer, AlertCircle, CheckCircle,
  Sparkles, Loader2, Video, FileText, Calendar, DollarSign, Target, Coffee, Lightbulb
} from "lucide-react";

const TOTAL_WEEKLY_HOURS = 168;

// Personal life categories (same as client)
const LIFE_CATEGORIES = [
  { id: "sleep_hours", name: "Sleep", icon: Moon, color: "bg-purple-500", defaultHours: 56 },
  { id: "exercise_hours", name: "Exercise", icon: Dumbbell, color: "bg-green-500", defaultHours: 5 },
  { id: "family_time_hours", name: "Family/Social", icon: Users, color: "bg-pink-500", defaultHours: 15 },
  { id: "personal_development_hours", name: "Personal Development", icon: BookOpen, color: "bg-amber-500", defaultHours: 3 },
  { id: "hobbies_hours", name: "Hobbies", icon: Palette, color: "bg-cyan-500", defaultHours: 4 },
  { id: "meals_self_care_hours", name: "Meals & Self-Care", icon: UtensilsCrossed, color: "bg-orange-500", defaultHours: 14 },
  { id: "commute_hours", name: "Commute", icon: Car, color: "bg-gray-500", defaultHours: 3 },
  { id: "errands_chores_hours", name: "Errands & Chores", icon: ShoppingCart, color: "bg-rose-500", defaultHours: 5 },
  { id: "buffer_hours", name: "Buffer Time", icon: Timer, color: "bg-neutral-500", defaultHours: 5 }
];

// Work breakdown categories (consultant-specific)
const WORK_CATEGORIES = [
  { id: "client_sessions_hours", name: "Client Sessions", icon: Video, color: "bg-blue-600", defaultHours: 15, description: "1-on-1 meetings, calls, workshops" },
  { id: "client_prep_hours", name: "Client Prep & Follow-up", icon: FileText, color: "bg-blue-400", defaultHours: 8, description: "Session notes, planning, documentation" },
  { id: "client_projects_hours", name: "Active Client Projects", icon: Target, color: "bg-indigo-500", defaultHours: 10, description: "Deliverables, research, strategy work" },
  { id: "admin_ops_hours", name: "Admin & Operations", icon: Calendar, color: "bg-slate-500", defaultHours: 5, description: "Invoicing, scheduling, emails" },
  { id: "marketing_bd_hours", name: "Marketing & Business Dev", icon: DollarSign, color: "bg-emerald-500", defaultHours: 5, description: "Content, networking, sales" },
  { id: "prof_development_hours", name: "Professional Development", icon: Lightbulb, color: "bg-yellow-500", defaultHours: 2, description: "Training, certifications, learning" },
  { id: "work_breaks_hours", name: "Work Breaks", icon: Coffee, color: "bg-amber-400", defaultHours: 3, description: "Lunch, short breaks during work" }
];

export const ConsultantTimeBudgetPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [budget, setBudget] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [notes, setNotes] = useState("");
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [newPriority, setNewPriority] = useState("");
  const [clientCount, setClientCount] = useState(0);

  useEffect(() => {
    fetchTimeBudget();
    fetchClientCount();
  }, []);

  const fetchTimeBudget = async () => {
    try {
      const response = await authFetch(`${API}/consultant/time-budget`, {  });
      if (response.ok) {
        const data = await response.json();
        setBudget(data);
        setCustomCategories(data.custom_categories || []);
        setPriorities(data.priorities || []);
        setNotes(data.notes || "");
        setAiRecommendations(data.ai_recommendations || null);
      }
    } catch (_error) {
      console.error("Error fetching time budget:", _error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClientCount = async () => {
    try {
      const response = await authFetch(`${API}/consultant/clients`, {  });
      if (response.ok) {
        const clients = await response.json();
        setClientCount(clients.length);
      }
    } catch (_error) {
      console.error("Error fetching clients:", _error);
    }
  };

  const calculateLifeTotal = () => {
    let total = 0;
    LIFE_CATEGORIES.forEach(cat => {
      total += parseFloat(budget[cat.id] || cat.defaultHours) || 0;
    });
    return total;
  };

  const calculateWorkTotal = () => {
    let total = 0;
    WORK_CATEGORIES.forEach(cat => {
      total += parseFloat(budget[cat.id] || cat.defaultHours) || 0;
    });
    customCategories.forEach(cat => {
      total += parseFloat(cat.hours) || 0;
    });
    return total;
  };

  const calculateTotal = () => calculateLifeTotal() + calculateWorkTotal();

  const remaining = TOTAL_WEEKLY_HOURS - calculateTotal();
  const percentageUsed = (calculateTotal() / TOTAL_WEEKLY_HOURS) * 100;
  const workPercentage = (calculateWorkTotal() / TOTAL_WEEKLY_HOURS) * 100;

  const handleSave = async () => {
    setSaving(true);
    try {
      const budgetData = {
        ...budget,
        custom_categories: customCategories,
        priorities,
        notes,
        ai_recommendations: aiRecommendations
      };

      const response = await authFetch(`${API}/consultant/time-budget`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(budgetData)
      });

      if (response.ok) {
        toast.success("Time budget saved!");
      } else {
        toast.error("Failed to save time budget");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateAI = async () => {
    setGeneratingAI(true);
    try {
      const currentBudget = {
        life_categories: LIFE_CATEGORIES.map(cat => ({
          name: cat.name,
          hours: parseFloat(budget[cat.id] || cat.defaultHours) || 0
        })),
        work_categories: WORK_CATEGORIES.map(cat => ({
          name: cat.name,
          hours: parseFloat(budget[cat.id] || cat.defaultHours) || 0,
          description: cat.description
        })),
        custom_categories: customCategories,
        total_hours: calculateTotal(),
        work_hours: calculateWorkTotal(),
        remaining_hours: remaining,
        client_count: clientCount,
        priorities,
        notes
      };

      const response = await authFetch(`${API}/consultant/time-budget/ai-recommendations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(currentBudget)
      });

      if (response.ok) {
        const data = await response.json();
        setAiRecommendations(data.recommendations);
        toast.success("AI recommendations generated!");
      } else {
        toast.error("Failed to generate recommendations");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setGeneratingAI(false);
    }
  };

  const addCustomCategory = () => {
    setCustomCategories([...customCategories, { name: "", hours: 0 }]);
  };

  const updateCustomCategory = (index, field, value) => {
    const updated = [...customCategories];
    updated[index][field] = value;
    setCustomCategories(updated);
  };

  const removeCustomCategory = (index) => {
    setCustomCategories(customCategories.filter((_, i) => i !== index));
  };

  const addPriority = () => {
    if (newPriority.trim()) {
      setPriorities([...priorities, newPriority.trim()]);
      setNewPriority("");
    }
  };

  const removePriority = (index) => {
    setPriorities(priorities.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-[#333333] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="consultant-time-budget">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/consultant")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-[#333333]">Consultant Time Budget</h1>
                <p className="text-sm text-neutral-500">Manage your 168-hour workweek for optimal client balance</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={handleGenerateAI}
                disabled={generatingAI}
                className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10"
                data-testid="generate-ai-btn"
              >
                {generatingAI ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                AI Recommendations
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#333333] hover:bg-[#444444] text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Budget
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* What is Time Budget - Explanation Card */}
        <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 mb-6">
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-emerald-900 mb-2">What is a Consultant Time Budget?</h3>
                <p className="text-emerald-800 mb-4">
                  As a consultant, your time is your most valuable asset. This tool helps you strategically allocate 
                  your <strong>168 weekly hours</strong> between client work, business development, and personal life—ensuring 
                  sustainable success without burnout.
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                    <h4 className="font-medium text-emerald-900 mb-1 flex items-center">
                      <Target className="w-4 h-4 mr-2 text-emerald-600" />
                      How It Works
                    </h4>
                    <ul className="text-sm text-emerald-700 space-y-1">
                      <li>• Separate work hours into client-facing vs. business ops</li>
                      <li>• Balance client sessions with prep and admin time</li>
                      <li>• Protect personal and recovery time</li>
                      <li>• Get AI insights based on your client load</li>
                    </ul>
                  </div>
                  <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
                    <h4 className="font-medium text-emerald-900 mb-1 flex items-center">
                      <Lightbulb className="w-4 h-4 mr-2 text-yellow-600" />
                      Benefits of Sticking to Your Budget
                    </h4>
                    <ul className="text-sm text-emerald-700 space-y-1">
                      <li>• Avoid overcommitting to clients</li>
                      <li>• Protect time for business growth activities</li>
                      <li>• Maintain energy for high-quality client delivery</li>
                      <li>• Build a sustainable consulting practice</li>
                    </ul>
                  </div>
                </div>
                
                <p className="text-sm text-emerald-600 italic">
                  💡 Pro Tip: For every client session hour, plan at least 30 minutes of prep and follow-up time.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Card */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-lg">
                  <Clock className="w-5 h-5 mr-2 text-[#39FF14]" />
                  Weekly Overview
                </CardTitle>
                <CardDescription>168 hours per week • {clientCount} active clients</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-neutral-600">Total Allocated</span>
                      <span className="font-medium">{calculateTotal().toFixed(1)} / 168 hours</span>
                    </div>
                    <Progress 
                      value={Math.min(percentageUsed, 100)} 
                      className={`h-3 ${percentageUsed > 100 ? '[&>div]:bg-red-500' : '[&>div]:bg-[#39FF14]'}`}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-700">{calculateWorkTotal().toFixed(1)}h</p>
                      <p className="text-xs text-blue-600">Work Hours</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-700">{calculateLifeTotal().toFixed(1)}h</p>
                      <p className="text-xs text-purple-600">Personal</p>
                    </div>
                    <div className={`p-3 rounded-lg ${remaining >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className={`text-2xl font-bold ${remaining >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {remaining.toFixed(1)}h
                      </p>
                      <p className={`text-xs ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {remaining >= 0 ? 'Remaining' : 'Over Budget'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Work Hours Breakdown */}
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Briefcase className="w-5 h-5 mr-2 text-blue-600" />
                  Work Hours Breakdown
                </CardTitle>
                <CardDescription>Break down your {calculateWorkTotal().toFixed(1)} work hours</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {WORK_CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    const hours = parseFloat(budget[category.id] || category.defaultHours) || 0;
                    return (
                      <div key={category.id} className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg">
                        <div className={`w-10 h-10 ${category.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Label className="text-sm font-medium">{category.name}</Label>
                          <p className="text-xs text-neutral-500 truncate">{category.description}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            min="0"
                            max="168"
                            step="0.5"
                            value={budget[category.id] ?? category.defaultHours}
                            onChange={(e) => setBudget({ ...budget, [category.id]: parseFloat(e.target.value) || 0 })}
                            className="w-20 text-center"
                          />
                          <span className="text-sm text-neutral-500">hrs</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Personal Life Categories */}
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Users className="w-5 h-5 mr-2 text-purple-600" />
                  Personal Life Hours
                </CardTitle>
                <CardDescription>Balance work with personal well-being</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {LIFE_CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    return (
                      <div key={category.id} className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg">
                        <div className={`w-10 h-10 ${category.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm font-medium">{category.name}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            min="0"
                            max="168"
                            step="0.5"
                            value={budget[category.id] ?? category.defaultHours}
                            onChange={(e) => setBudget({ ...budget, [category.id]: parseFloat(e.target.value) || 0 })}
                            className="w-20 text-center"
                          />
                          <span className="text-sm text-neutral-500">hrs</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Custom Categories */}
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-lg">
                  <span className="flex items-center">
                    <Plus className="w-5 h-5 mr-2 text-[#39FF14]" />
                    Custom Categories
                  </span>
                  <Button variant="outline" size="sm" onClick={addCustomCategory}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {customCategories.length === 0 ? (
                  <p className="text-sm text-neutral-500 text-center py-4">
                    No custom categories. Add one for specialized time tracking.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {customCategories.map((cat, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg">
                        <Input
                          placeholder="Category name"
                          value={cat.name}
                          onChange={(e) => updateCustomCategory(index, "name", e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          value={cat.hours}
                          onChange={(e) => updateCustomCategory(index, "hours", parseFloat(e.target.value) || 0)}
                          className="w-20"
                        />
                        <span className="text-sm text-neutral-500">hrs</span>
                        <Button variant="ghost" size="sm" onClick={() => removeCustomCategory(index)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* AI Recommendations */}
            <Card className={`border-2 ${aiRecommendations ? 'border-[#39FF14]/30 bg-[#39FF14]/5' : 'border-neutral-200'}`}>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Sparkles className="w-5 h-5 mr-2 text-[#39FF14]" />
                  AI Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {aiRecommendations ? (
                  <div className="space-y-4">
                    {aiRecommendations.summary && (
                      <div className="p-3 bg-white rounded-lg border border-[#39FF14]/20">
                        <p className="text-sm text-neutral-700">{aiRecommendations.summary}</p>
                      </div>
                    )}
                    {aiRecommendations.suggestions && aiRecommendations.suggestions.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-neutral-700">Suggestions:</p>
                        {aiRecommendations.suggestions.map((suggestion, idx) => (
                          <div key={idx} className="flex items-start space-x-2 text-sm">
                            <CheckCircle className="w-4 h-4 text-[#39FF14] flex-shrink-0 mt-0.5" />
                            <span className="text-neutral-600">{suggestion}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {aiRecommendations.warnings && aiRecommendations.warnings.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-amber-700">Watch Out:</p>
                        {aiRecommendations.warnings.map((warning, idx) => (
                          <div key={idx} className="flex items-start space-x-2 text-sm">
                            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            <span className="text-neutral-600">{warning}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {aiRecommendations.client_capacity && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-700">Client Capacity</p>
                        <p className="text-xs text-blue-600 mt-1">{aiRecommendations.client_capacity}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Sparkles className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                    <p className="text-sm text-neutral-500 mb-3">
                      Get personalized recommendations based on your time allocation
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateAI}
                      disabled={generatingAI}
                      className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10"
                    >
                      {generatingAI ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                      Generate
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Weekly Priorities */}
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Target className="w-5 h-5 mr-2 text-[#333333]" />
                  Weekly Priorities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Add a priority..."
                      value={newPriority}
                      onChange={(e) => setNewPriority(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addPriority()}
                    />
                    <Button variant="outline" size="sm" onClick={addPriority}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {priorities.length > 0 ? (
                    <div className="space-y-2">
                      {priorities.map((priority, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg">
                          <span className="text-sm">{index + 1}. {priority}</span>
                          <Button variant="ghost" size="sm" onClick={() => removePriority(index)} className="text-red-500 h-6 w-6 p-0">
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-500 text-center py-2">No priorities set</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Weekly reflections, adjustments needed, etc."
                  className="min-h-24"
                />
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card className="border-neutral-200 bg-[#333333] text-white">
              <CardContent className="p-4">
                <h4 className="font-medium mb-3">Time Per Client</h4>
                {clientCount > 0 ? (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-300">Sessions</span>
                      <span>{((budget.client_sessions_hours || 15) / clientCount).toFixed(1)} hrs/client</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-300">Prep/Follow-up</span>
                      <span>{((budget.client_prep_hours || 8) / clientCount).toFixed(1)} hrs/client</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-300">Total per client</span>
                      <span className="text-[#39FF14] font-medium">
                        {(((budget.client_sessions_hours || 15) + (budget.client_prep_hours || 8) + (budget.client_projects_hours || 10)) / clientCount).toFixed(1)} hrs
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-neutral-400">No active clients</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantTimeBudgetPage;
